import { useCallback, useEffect, useState } from 'react';

import { exportCertificates } from '../api/exportCertificatesApiService';
import type { ExportCertificatesParams } from '../types/myCertificateTypes';

/** Return values exposed by {@link useCertificateExport}. */
export interface UseCertificateExportReturn {
  /** True while an export request is in flight. */
  isExporting: boolean;
  /** 1–3 — number of trailing dots for the animated in-progress snackbar. */
  exportDotCount: number;
  /** Success snackbar visibility + close handler. */
  exportSuccessOpen: boolean;
  closeExportSuccess: () => void;
  /** Error snackbar visibility + close handler. */
  exportErrorOpen: boolean;
  closeExportError: () => void;
  /** "Nothing to export" snackbar visibility + close handler. */
  exportEmptyOpen: boolean;
  closeExportEmpty: () => void;
  /**
   * Runs the export flow: short-circuits to the empty snackbar when
   * `itemCount` is 0 (no API call), otherwise calls the export API with
   * `params`, opens the presigned URL in a new tab on success, and maps a
   * 404 to the empty snackbar / any other failure to the error snackbar.
   */
  handleExport: (
    params: ExportCertificatesParams,
    itemCount: number
  ) => Promise<void>;
}

/**
 * Shared certificate-list export flow — in-flight state, animated-dots
 * counter and success/error/empty snackbar state. Used by the technician
 * "Moje certyfikaty" page and the client Certyfikaty tab; each caller builds
 * its own filter params and renders its own button/snackbars.
 */
export function useCertificateExport(): UseCertificateExportReturn {
  const [isExporting, setIsExporting] = useState(false);
  const [exportDotCount, setExportDotCount] = useState(1);
  const [exportSuccessOpen, setExportSuccessOpen] = useState(false);
  const [exportErrorOpen, setExportErrorOpen] = useState(false);
  const [exportEmptyOpen, setExportEmptyOpen] = useState(false);

  // Cycles the loading snackbar's trailing dots ("." -> ".." -> "...")
  // for as long as an export is in progress — matches the Figma 3-frame
  // loading animation.
  useEffect(() => {
    if (!isExporting) {
      return;
    }
    setExportDotCount(1);
    const interval = setInterval(() => {
      setExportDotCount((prev) => (prev % 3) + 1);
    }, 500);
    return () => clearInterval(interval);
  }, [isExporting]);

  const handleExport = useCallback(
    async (params: ExportCertificatesParams, itemCount: number) => {
      if (itemCount === 0) {
        setExportEmptyOpen(true);
        return;
      }

      setIsExporting(true);
      try {
        const result = await exportCertificates(params);
        window.open(result.download_url, '_blank', 'noopener,noreferrer');
        setExportSuccessOpen(true);
      } catch (error) {
        const status = (error as { status?: number }).status;
        if (status === 404) {
          setExportEmptyOpen(true);
        } else {
          setExportErrorOpen(true);
        }
      } finally {
        setIsExporting(false);
      }
    },
    []
  );

  return {
    isExporting,
    exportDotCount,
    exportSuccessOpen,
    closeExportSuccess: useCallback(() => setExportSuccessOpen(false), []),
    exportErrorOpen,
    closeExportError: useCallback(() => setExportErrorOpen(false), []),
    exportEmptyOpen,
    closeExportEmpty: useCallback(() => setExportEmptyOpen(false), []),
    handleExport,
  };
}
